import { web3Enable, web3FromSource, web3Accounts } from '@polkadot/extension-dapp';
import type { InjectedAccountWithMeta, InjectedExtension } from '@polkadot/extension-inject/types';

export interface WalletInfo {
  address: string;
  chainId: string;
  balance: string;
  isConnected: boolean;
  tokenSymbol: string;
  provider: string;
  account?: InjectedAccountWithMeta;
}

export interface SubstrateWalletConfig {
  appName: string;
  network: {
    name: string;
    rpcUrl: string;
    chainId: string;
    tokenSymbol: string;
  };
}

const defaultConfig: SubstrateWalletConfig = {
  appName: 'Bridge App',
  network: {
    name: 'Analog Testnet',
    rpcUrl: 'wss://rpc.testnet.analog.one',
    chainId: '12850',
    tokenSymbol: 'ANLOG',
  },
};

class SubstrateWalletService {
  private config: SubstrateWalletConfig;
  private currentWallet: WalletInfo | null = null;
  private currentInjector: InjectedExtension | null = null;
  private readonly STORAGE_KEY = 'bridge_wallet_state';

  constructor(config: Partial<SubstrateWalletConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
    this.loadPersistedWallet();
  }

  private loadPersistedWallet(): void {
    try {
      const persisted = localStorage.getItem(this.STORAGE_KEY);
      if (persisted) {
        const walletData = JSON.parse(persisted);
        this.currentWallet = walletData;
      }
    } catch (error) {
      console.error('Failed to load persisted wallet:', error);
    }
  }

  private persistWallet(wallet: WalletInfo | null): void {
    try {
      if (wallet) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(wallet));
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      console.error('Failed to persist wallet:', error);
    }
  }

  async enableWallet(): Promise<void> {
    try {
      await web3Enable(this.config.appName);
    } catch (error) {
      console.error('Failed to enable wallet extensions:', error);
      throw new Error('No wallet extension found. Please install Subwallet, Talisman, or Polkadot.js extension.');
    }
  }

  async getAvailableWallets(): Promise<string[]> {
    await this.enableWallet();
    const accounts = await web3Accounts();
    const sources = [...new Set(accounts.map((acc: any) => acc.meta.source))];
    return sources;
  }

  async connectWallet(selectedWallet: string, selectedAccount?: InjectedAccountWithMeta, setSelectedAccount?:React.Dispatch<InjectedAccountWithMeta | undefined>): Promise<WalletInfo> {
    try {
      await this.enableWallet();
      
      const availableWallets = await this.getAvailableWallets();
      if (!availableWallets.includes(selectedWallet)) {
        throw new Error(`Wallet ${selectedWallet} is not available. Available wallets: ${availableWallets.join(', ')}`);
      }
      
      this.currentInjector = await web3FromSource(selectedWallet);
      
      if (!this.currentInjector) {
        throw new Error(`Wallet ${selectedWallet} not found or not available`);
      }

      const accounts = await web3Accounts();
      const walletAccounts = accounts.filter((acc: any) => acc.meta.source === selectedWallet);
      
      if (walletAccounts.length === 0) {
        throw new Error(`No accounts found in ${selectedWallet}. Please create an account first.`);
      }

      // Use the provided account or the first one
      const accountToUse = selectedAccount || walletAccounts[0];
      if (setSelectedAccount) {
        setSelectedAccount(accountToUse);
      }
      
      this.currentWallet = {
        address: accountToUse.address,
        chainId: this.config.network.chainId,
        balance: '0',
        isConnected: true,
        tokenSymbol: this.config.network.tokenSymbol,
        provider: selectedWallet,
        account: accountToUse,
      };

      this.persistWallet(this.currentWallet);

      return this.currentWallet;
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      throw error;
    }
  }

  async disconnectWallet(setSelectedAccount: React.Dispatch<InjectedAccountWithMeta | undefined>): Promise<void> {
    this.currentWallet = null;
    this.currentInjector = null;
    this.persistWallet(null);
    setSelectedAccount(undefined)
    localStorage.removeItem('ConnectedAccount');
  }

  async getBalance(_address: string): Promise<string> {
    return '0';
  }

  async switchNetwork(chainId: string): Promise<void> {
    this.config.network.chainId = chainId;
    
    if (this.currentWallet) {
      this.currentWallet.chainId = chainId;
      this.persistWallet(this.currentWallet);
    }
  }

  getCurrentWallet(): WalletInfo | null {
    return this.currentWallet;
  }

  getCurrentInjector(): InjectedExtension | null {
    return this.currentInjector;
  }

  getConfig() : SubstrateWalletConfig{
    return this.config;
  } 

  async getAccounts(walletSource?: string): Promise<any[]> {
    await this.enableWallet();
    const accounts = await web3Accounts();
    
    if (walletSource) {
      return accounts.filter((acc: any) => acc.meta.source === walletSource);
    }
    
    return accounts;
  }
}

export const substrateWalletService = new SubstrateWalletService();
